import React, { useCallback, useEffect, useMemo } from 'react';
import Joi from 'joi';
import { useDispatch, useSelector } from 'react-redux';

import { userSelectors } from 'reducers';
import { validationHandlerSet } from 'reducers/tunnel';
import { activities } from 'utils/formData';
import { validateSchema } from 'utils/helpers';
import { EMAIL_VALIDATION_OPTIONS, NAME_REGEX } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { TunnelBox } from 'containers/tunnel/common';
import {
    MemoizedCorporateForm,
    MemoizedFaxForm,
    MemoizedFiscalSituationFormFPCIPM,
    MemoizedGeneralInfoForm,
    MemoizedPoliticallyExposedForm,
    MemoizedSituationFormFPCIPM,
    MemoizedSubscriberDeclarationForm,
} from './memoized-components/MemoizedComponents';
import { ErrorMessage, FullAddressForm, SubmitButton, SuccessMessage } from 'components';

const schema = {
    firstName: Joi.string().required().trim().min(2).max(120).pattern(NAME_REGEX).messages({
        'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_PRENOM,
        'string.min': texts.FIRST_NAME_LENGTH,
        'string.max': texts.FIRST_NAME_LENGTH,
        'string.pattern.base': texts.FIRST_NAME_PATTERN,
    }),
    lastName: Joi.string().required().trim().min(2).max(120).pattern(NAME_REGEX).messages({
        'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_NOM,
        'string.min': texts.LAST_NAME_LENGTH,
        'string.max': texts.LAST_NAME_LENGTH,
        'string.pattern.base': texts.LAST_NAME_PATTERN,
    }),
    birthName: Joi.string().trim().max(50).pattern(NAME_REGEX).allow('').messages({
        'string.max': texts.VEUILLEZ_SAISIR_VOTRE_NOM,
        'string.pattern.base': texts.BIRTHNAME_PATTERN,
    }),
    email: Joi.string().required().email(EMAIL_VALIDATION_OPTIONS).messages({
        'string.empty': texts.SAISIR_LE_CHAMP,
        'string.email': texts.ADRESSE_EMAIL_SAISIE_PAS_VALIDE,
    }),
    activity: Joi.string().required().messages({
        'string.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
        'string.empty': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
    }),
    otherActivity: Joi.when('activity', {
        is: Joi.string().equal(activities.other),
        then: Joi.string().required(),
        otherwise: Joi.any(),
    }),
    currentAddress: {
        address: Joi.string()
            .required()
            .messages({ 'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_ADDRESS }),
        city: Joi.string().required().messages({ 'string.empty': texts.VEUILLEZ_SAISIR_LA_VILLE }),
        zipCode: Joi.string()
            .required()
            .messages({ 'string.empty': texts.VEUILLEZ_SAISIR_LE_CODE_POSTAL }),
    },
    shareCapital: Joi.number().required().messages({ 'number.base': texts.SAISIR_LE_CHAMP }),
    signatoryOnKbis: Joi.string().required().messages({
        'string.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
        'string.empty': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
    }),
    representativeRole: Joi.string()
        .required()
        .messages({ 'string.empty': texts.VOUS_DEVEZ_REMPLIR_LE_CHAMP }),
    representativeName: Joi.when('signatoryOnKbis', {
        is: 'no',
        then: Joi.string().required(),
        otherwise: Joi.any(),
    }).messages({ 'string.empty': texts.VOUS_DEVEZ_REMPLIR_LE_CHAMP }),
    ownAccountAgreement: Joi.string().required().messages({
        'string.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
        'string.empty': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
    }),
    politicallyExposed: Joi.object({
        politicallyExposedPerson: Joi.string().valid('true', 'false').required(),
    }),
};

const ContactInfoFormPM = ({
    isStepVerified,
    isPhoneVerified,
    forms,
    callbacks,
    onSubmit,
    errors,
    tunnelErrors,
    validateDefaultSchema,
    areDefaultFieldsCompleted,
    submitLoader,
    changesMade,
    handleNextStep,
    tunnelSubscription,
    onAdditionalErrors,
    showSuccessMessage,
    fileSizeError,
    fileTypeError,
    isOnClientSpace,
    isOnAdminSpace,
}) => {
    const dispatch = useDispatch();
    const internalUser = useSelector(userSelectors.getInternalUser);

    const isImpersonated = !!internalUser?.impersonatedByUser;

    const areSpecificFieldsCompleted = useMemo(
        () =>
            tunnelSubscription.indirectUntransferred
                ? true
                : (forms.corporateInfo.hasDifferentPostalAddress === false ||
                      (forms.corporateInfo.postalAddress.address !== '' &&
                          forms.corporateInfo.postalAddress.city !== '' &&
                          forms.corporateInfo.postalAddress.zipCode !== '' &&
                          forms.corporateInfo.postalAddress.country !== '')) &&
                  (forms.corporateInfo.activity !== activities.other ||
                      (forms.corporateInfo.activity === activities.other &&
                          forms.corporateInfo.otherActivity !== '')) &&
                  forms.corporateInfo.currentAddress.country &&
                  forms.corporateInfo.currentAddress.city &&
                  forms.corporateInfo.currentAddress.zipCode &&
                  forms.corporateInfo.currentAddress.address &&
                  forms.corporateInfo.shareCapital &&
                  forms.corporateInfo.rcsRegistrationAddress &&
                  (forms.corporateInfo.rcsNumber ||
                      forms.corporateInfo.legalEntityIdentificationNumber) &&
                  forms.corporateInfo.signatoryOnKbis &&
                  (forms.corporateInfo.signatoryOnKbis === 'yes'
                      ? forms.corporateInfo.representativeRole
                      : forms.corporateInfo.representativeName &&
                        forms.corporateInfo.representativeRole) &&
                  !!forms.politicallyExposed?.politicallyExposedPerson &&
                  (forms.politicallyExposed?.politicallyExposedPerson === 'false' ||
                      Object.values(forms.politicallyExposed?.politicallyExposedReason).some(
                          per => per === true
                      )),
        [forms, tunnelSubscription]
    );

    const allowSubmit = useMemo(
        () => areDefaultFieldsCompleted && areSpecificFieldsCompleted,
        [areDefaultFieldsCompleted, areSpecificFieldsCompleted]
    );

    const submitButtonText = useMemo(() => {
        if (isOnClientSpace) {
            return texts.SAVE;
        }
        return isStepVerified && changesMade ? texts.SAVE_AND_CONTINUE : texts.SUIVANT;
    }, [isOnClientSpace, isStepVerified, changesMade]);

    const formIsValid = useCallback(() => {
        const isDefaultSchemaValid = validateDefaultSchema();
        if (tunnelSubscription.indirectUntransferred) return isDefaultSchemaValid;
        let { valid, newErrors } = validateSchema(schema, {
            ...forms.corporateInfo,
            ...forms.generalInfo,
        });
        if (forms.politicallyExposed.politicallyExposedPerson === 'true') {
            const containsAtLeastOneTrue = Object.keys(
                forms.politicallyExposed.politicallyExposedReason
            ).some(key => forms.politicallyExposed.politicallyExposedReason[key] === true);
            valid = valid && containsAtLeastOneTrue;
        }
        onAdditionalErrors(newErrors);
        return isDefaultSchemaValid && valid;
    }, [validateDefaultSchema, onAdditionalErrors, tunnelSubscription, forms]);

    const handleSubmit = useCallback(() => {
        onSubmit(formIsValid());
    }, [formIsValid, onSubmit]);

    useEffect(() => {
        dispatch(validationHandlerSet(formIsValid));
        return () => dispatch(validationHandlerSet());
    }, [dispatch, formIsValid]);

    return (
        <React.Fragment>
            <TunnelBox>
                {(!!Object.keys(tunnelErrors).length ||
                    Object.keys(errors).find(key => errors[key] !== '')) && (
                    <ErrorMessage gutterBottom message={texts.FORM_HAS_ERRORS} />
                )}
                <form onSubmit={e => e.preventDefault()}>
                    <MemoizedCorporateForm
                        corporateInfo={forms.corporateInfo}
                        onChange={callbacks.handleCorporateInfoChange}
                        onFileUpload={callbacks.handleFileUpload}
                        onCorporateAddressChange={callbacks.handleCorporateAddressChange}
                        onCorporatePostalAddressChange={
                            callbacks.handleCorporatePostalAddressChange
                        }
                        errors={errors}
                        tunnelErrors={tunnelErrors}
                        hasDifferentPostalAddress={forms.corporateInfo.hasDifferentPostalAddress}
                        toggleCorporatePostalAddress={
                            callbacks.toggleCorporateHasDifferentPostalAddress
                        }
                        displayAllFields={isOnClientSpace || isOnAdminSpace}
                        fileSizeError={fileSizeError}
                        fileTypeError={fileTypeError}
                    />
                    <MemoizedGeneralInfoForm
                        generalInfo={forms.generalInfo}
                        onChange={callbacks.handleGeneralInfoChange}
                        errors={errors}
                        isPhoneVerified={isPhoneVerified}
                        tunnelErrors={tunnelErrors}
                        displayAllFields={isOnClientSpace || isOnAdminSpace}
                    />
                    <MemoizedFaxForm
                        onChange={callbacks.handleGeneralInfoChange}
                        fax={forms.generalInfo?.fax}
                    />
                    <FullAddressForm
                        address={forms.address}
                        label={texts.ADRESSE_DOMICILE_SIGNATAIRE}
                        onAddressChange={callbacks.handleAddressChange}
                        errors={errors}
                        tunnelErrors={tunnelErrors}
                    />
                    <MemoizedSituationFormFPCIPM
                        situation={forms.situation}
                        onChange={callbacks.handleSituationChange}
                        errors={errors}
                        tunnelErrors={tunnelErrors}
                    />
                    <MemoizedFiscalSituationFormFPCIPM
                        fiscalSituation={forms.fiscalSituation}
                        onChange={callbacks.handleFiscalSituationChange}
                        errors={errors}
                    />
                    <MemoizedSubscriberDeclarationForm
                        isReadOnly={isOnClientSpace && !isImpersonated}
                        ownAccountAgreement={forms.corporateInfo?.ownAccountAgreement}
                        onChange={callbacks.handleCorporateInfoChange}
                        errors={errors}
                    />
                    <MemoizedPoliticallyExposedForm
                        politicallyExposed={forms.politicallyExposed}
                        onChange={callbacks.handleChangePoliticallyExposedPerson}
                        onChangeReason={callbacks.handleChangePoliticallyExposedReason}
                    />
                </form>
            </TunnelBox>
            {showSuccessMessage && <SuccessMessage message={texts.YOUR_INFO_SAVED} />}
            <SubmitButton
                text={submitButtonText}
                onClick={
                    changesMade || ((isOnClientSpace || isOnAdminSpace) && changesMade)
                        ? handleSubmit
                        : handleNextStep
                }
                disabled={
                    (!allowSubmit && !tunnelSubscription.indirectUntransferred) || submitLoader
                }
                loading={submitLoader}
            />
        </React.Fragment>
    );
};

export default ContactInfoFormPM;
